import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Plus,
  Search,
  RefreshCw,
  Paperclip,
  Trash2,
  FileEdit,
  Loader2,
} from "lucide-react";
import {
  useGetMailCampaignsQuery,
  useDeleteMailCampaignMutation,
  type CampaignStatus,
  type MailCampaign,
} from "../api/mailApi";

const TABS: { key: string; label: string }[] = [
  { key: "", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "sending", label: "Sending" },
  { key: "sent", label: "Sent" },
];

function StatusPill({ status }: { status: CampaignStatus }) {
  const tone =
    status === "sent"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "sending"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-gray-50 text-gray-500 border-gray-200";

  return (
    <span
      className={`inline-flex items-center rounded-lg border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.15em] ${tone}`}
    >
      {status}
    </span>
  );
}

/** How far a send has got. A draft has nothing to report yet. */
function Progress({ campaign }: { campaign: MailCampaign }) {
  if (campaign.status === "draft") return <span className="text-xs text-gray-300">—</span>;

  const total = campaign.recipients_count || 0;
  const done = campaign.sent_count + campaign.failed_count;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="min-w-[140px]">
      <div className="flex items-center justify-between text-[10px] font-bold">
        <span className="text-gray-600">
          {campaign.sent_count} / {total}
        </span>
        {campaign.failed_count > 0 && (
          <span className="text-rose-600">{campaign.failed_count} failed</span>
        )}
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
        <div
          className={`h-full rounded-full ${campaign.failed_count ? "bg-amber-500" : "bg-emerald-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const { data, isLoading, isFetching, refetch } = useGetMailCampaignsQuery(
    { status: status || undefined, search: search || undefined },
    // A send in flight moves on its own, so the list keeps itself honest.
    { pollingInterval: 15000 },
  );

  const [remove] = useDeleteMailCampaignMutation();

  const campaigns = data?.data ?? [];

  const onDelete = async (campaign: MailCampaign) => {
    if (!window.confirm(`Delete the draft "${campaign.subject}"?`)) return;
    try {
      await remove(campaign.id).unwrap();
    } catch (e: any) {
      window.alert(e?.data?.message || "The draft could not be deleted.");
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-4 md:p-6 pb-20">
      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <Mail className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">
                Institute Mail
              </h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">
                Compose a message and send it to any mix of groups.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => refetch()}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-indigo-500 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={() => navigate("/admin/mail/compose")}
              className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-primary transition-all active:scale-95 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" /> Compose
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatus(tab.key)}
                className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-[0.15em] transition-all ${
                  status === tab.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 flex-1">
            <Search className="w-3.5 h-3.5 text-gray-300" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by subject…"
              className="w-full py-2.5 text-xs outline-none bg-transparent placeholder:text-gray-300"
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400 p-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-8 h-8 text-gray-200 mx-auto" />
            <p className="text-sm font-bold text-gray-500 mt-3">Nothing here yet.</p>
            <p className="text-xs text-gray-400 mt-1">
              Compose a message to send it to a chapter, a committee, or the whole register.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  {["Subject", "Status", "Delivery", "Composed by", "Date", ""].map((h) => (
                    <th
                      key={h}
                      className="text-left px-6 py-4 text-[9px] font-black uppercase tracking-[0.15em] text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <button
                        onClick={() =>
                          navigate(
                            c.status === "draft"
                              ? `/admin/mail/compose/${c.id}`
                              : `/admin/mail/campaigns/${c.id}`,
                          )
                        }
                        className="text-left"
                      >
                        <p className="text-sm font-bold text-gray-900 hover:text-indigo-600 transition-colors">
                          {c.subject}
                        </p>
                      </button>
                      {(c.attachments_count ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-400 mt-1">
                          <Paperclip className="w-3 h-3" /> {c.attachments_count}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <StatusPill status={c.status} />
                    </td>
                    <td className="px-6 py-4">
                      <Progress campaign={c} />
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">{c.creator?.name ?? "—"}</td>
                    <td className="px-6 py-4 text-xs text-gray-400">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {c.status === "draft" ? (
                          <>
                            <button
                              onClick={() => navigate(`/admin/mail/compose/${c.id}`)}
                              title="Edit draft"
                              className="p-2 rounded-lg hover:bg-white hover:shadow-sm transition-all"
                            >
                              <FileEdit className="w-3.5 h-3.5 text-gray-500" />
                            </button>
                            <button
                              onClick={() => onDelete(c)}
                              title="Delete draft"
                              className="p-2 rounded-lg hover:bg-rose-50 transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => navigate(`/admin/mail/campaigns/${c.id}`)}
                            className="text-[10px] font-black uppercase tracking-[0.15em] text-indigo-600 hover:text-indigo-800 px-2"
                          >
                            Report
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
